import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faSpinner, faRotate } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const MostActiveStocks = ({ onSymbolSelect }) => {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadMostActive = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await marketDataApi.getMostActiveStocks();
      setStocks(response.data || []);
    } catch (err) {
      console.error('Error loading most active stocks:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load most active stocks');
      setStocks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMostActive();
  }, []);

  const formatNumber = (value) => {
    if (!value && value !== 0) return 'N/A';
    if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
    if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
    if (value >= 1e3) return (value / 1e3).toFixed(2) + 'K';
    return value.toLocaleString();
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-1.5">
          <FontAwesomeIcon icon={faChartLine} className="text-xs" />
          Most Active
        </h2>
        <button
          onClick={loadMostActive}
          disabled={loading}
          className="px-2 py-1 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
        >
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xs" />
          ) : (
            <FontAwesomeIcon icon={faRotate} className="text-xs" />
          )}
        </button>
      </div>

      {error && (
        <div className="mb-2 p-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs">
          {error}
        </div>
      )}

      {loading && stocks.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-xs">
          <FontAwesomeIcon icon={faSpinner} className="animate-spin mb-2" />
          Loading...
        </div>
      ) : stocks.length === 0 ? (
        <div className="text-center py-8 text-gray-500 text-xs">No data available</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Symbol</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Volume</th>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Trade Count</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stocks.map((stock, index) => (
                <tr 
                  key={stock.symbol || index} 
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (onSymbolSelect) onSymbolSelect(stock.symbol);
                    else navigate(`/asset/${stock.symbol}`);
                  }}
                >
                  <td className="px-2 py-1.5 font-semibold text-indigo-600">{stock.symbol}</td>
                  <td className="px-2 py-1.5 text-gray-700">{formatNumber(stock.volume || 0)}</td>
                  <td className="px-2 py-1.5 text-gray-700">{formatNumber(stock.trade_count || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MostActiveStocks;

